import RefreshIcon from '@/assets/icons/Refresh';
import { ROUTE_DOCS_MONITORING } from '@/common/constants';
import { InputControl } from '@/components/controls/input-control';
import SelectControl from '@/components/controls/select-control';
import MultiSelectControl from '@/components/controls/multi-select';
import useFilter from '@/components/filter/useFilter';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { MONTHS_LIST } from '@/features/agro-technical-events/constants/month';
import { useMonitoringStore } from '@/features/monitoring/store/useMonitoringStore';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'pending' },
  { value: 'APPROVED', label: 'approved' },
  { value: 'CHECKED', label: 'partially_approved' },
];

interface IMonitoringFilterProps {
  className?: string;
}

const MonitoringFilter: FC<IMonitoringFilterProps> = ({ className }) => {
  const { t } = useTranslation();
  const { region_id } = useParams();
  const [searchParams] = useSearchParams();
  const { form, control, handleForm, clearFilter } = useFilter({
    page: ROUTE_DOCS_MONITORING,
  });
  const { fetchActionList, actionListData, actionListLoading } =
    useMonitoringStore();

  const monthValue = form.watch('project_month');
  const plantId = searchParams.get('plant_id') || '1';
  const year = searchParams.get('year') || '2026';

  useEffect(() => {
    if (monthValue && monthValue !== 'all' && region_id) {
      fetchActionList({
        plant_id: plantId,
        region_id,
        month: monthValue,
        year,
      });
    }
  }, [monthValue, plantId, region_id, year]);

  const monthOptions = useMemo(
    () => MONTHS_LIST.map((m) => ({ value: m.value, label: t(m.label) })),
    [t],
  );

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map((s) => ({ value: s.value, label: t(s.label) })),
    [t],
  );

  const actionOptions = useMemo(
    () => actionListData.map((item) => ({ value: item.id, label: item.name })),
    [t, actionListData],
  );

  return (
    <Form {...form}>
      <form
        onSubmit={handleForm}
        className={cn(
          'border-b border-gray-200 py-2 pr-10 pl-6 bg-white',
          className,
        )}
      >
        <div className="flex items-end gap-3">
          <InputControl
            control={control}
            labelClass="text-xs"
            name="inn"
            inputClass="h-8"
            label={t('inn')}
            className="min-w-[140px] w-[200px] mb-5"
            type="number"
            maxLength={9}
            withoutDescription
          />

          <SelectControl
            control={control}
            name="project_month"
            label={t('month')}
            options={monthOptions}
            className="min-w-[140px] w-[180px]"
            selectClass="h-8"
            labelClass="text-xs"
            onChange={() => form.setValue('project_action_id', [])}
          />
          <MultiSelectControl
            control={control}
            name="project_action_id"
            label={t('events')}
            options={actionOptions}
            disabled={!monthValue || monthValue === 'all'}
            placeholder={
              monthValue && monthValue !== 'all' ? '' : 'Oyni tanlang'
            }
            className="min-w-[140px] w-[180px]"
            selectClass="h-8"
            labelClass="text-xs"
          />
          <SelectControl
            control={control}
            name="project_status"
            label={t('status')}
            options={statusOptions}
            className="min-w-[140px] w-[180px]"
            selectClass="h-8"
            labelClass="text-xs"
          />

          <div className="flex gap-3 mb-5">
            <Button
              type="submit"
              variant="outline"
              className="p-[10px] active:translate-y-[1px] duration-200"
            >
              <Search className="stroke-cyan-default" />
            </Button>
            <Button
              type="button"
              onClick={() => clearFilter()}
              variant="outline"
              className="p-[10px] active:translate-y-[1px] duration-200"
            >
              <RefreshIcon />
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default MonitoringFilter;
